import React, { useState } from "react";
import { ChevronLeft, Loader2, SquareTerminal } from "lucide-react";
import { toast } from "sonner";

import { personName } from "@/shared/api/members";
import { useApproveSkill, useAttachSkill, useSkill, useSkillFile, useSkills, useUpdateSkillLink } from "@/shared/api/skills";
import CodeBlock from "@/shared/components/markdown/CodeBlock";
import { AuditPill, AuditShield, SourceBadge } from "@/shared/components/skill-atoms";
import { BrandButton, Chip, GhostButton, IconButton, SearchInput } from "@/shared/components/ui-kit";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { useSettled } from "@/shared/hooks/useSettled";

import type { AgentSkill, PersonRef, Skill, SkillDetail } from "@/shared/types";

/* Put a catalog skill on an agent, approve one somebody asked for, or take a recipe's new
   steps (backend/modules/skills/README.md, "A skill on an agent"). Search the catalog, pick one, and see
   what its security auditors found — or that none has looked — and whether it needs the
   sandbox, before saying yes; a recipe shows the very steps the yes is for. The owner's yes
   attaches it at their own risk; anyone else's asks the owner. */

type Mode = "attach" | "request" | "approve" | "update";

const PRIMARY: Record<Mode, string> = { attach: "Attach", request: "Request", approve: "Approve", update: "Update" };

export default function SkillDialog({ agentId, sandboxOn, owner, isOwner, skills, approving, updating, onClose }: {
  agentId: string;
  /** Without it, a skill's scripts don't run: it gets the instructions only. */
  sandboxOn: boolean;
  owner: PersonRef;
  isOwner: boolean;
  /** The agent's skills: one it has, or has asked for, can't be picked again. */
  skills: AgentSkill[];
  /** A request to approve: the dialog opens on it rather than on the search. */
  approving?: AgentSkill;
  /** A recipe whose newest steps the owner reads before taking them: opens on it too. */
  updating?: AgentSkill;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<Skill | null>(approving ?? updating ?? null);
  const [query, setQuery] = useState(""); // kept here, so Back finds the search as it was
  const mode: Mode = approving ? "approve" : updating ? "update" : isOwner ? "attach" : "request";
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 gap-0 w-[480px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        {picked ? (
          <SkillConfirm
            skill={picked}
            mode={mode}
            agentId={agentId}
            sandboxOn={sandboxOn}
            owner={owner}
            requester={approving?.link.requested_by ?? null}
            askedVersion={approving?.link.version}
            onBack={approving || updating ? undefined : () => setPicked(null)}
            onDone={onClose}
          />
        ) : (
          <SkillSearch query={query} onQuery={setQuery} owner={owner} isOwner={isOwner} skills={skills} onPick={setPicked} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SkillSearch({ query, onQuery, owner, isOwner, skills, onPick }: {
  query: string;
  onQuery: (query: string) => void;
  owner: PersonRef;
  isOwner: boolean;
  skills: AgentSkill[];
  onPick: (skill: Skill) => void;
}) {
  const q = useSettled(query.trim());
  const list = useSkills({ q: q || undefined });
  const found = list.data?.pages.flatMap((p) => p.items) ?? [];
  const status = (id: string) => skills.find((s) => s.id === id)?.link.status;

  return (
    <>
      <div className="px-5 pt-5 pb-3 pr-12">
        <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Add a skill</DialogTitle>
        <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">
          {isOwner
            ? "Pick one to see what its security auditors found before you attach it."
            : `Pick one to ask ${personName(owner)}, who owns this agent, for it.`}
        </DialogDescription>
      </div>
      <div className="px-5 pb-3">
        <SearchInput autoFocus value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Search the catalog…" aria-label="Search skills" />
      </div>
      <div className="mx-5 mb-5 rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] overflow-hidden">
        <div className="max-h-[320px] overflow-y-auto divide-y divide-[var(--hairline)]">
          {list.isPending && <div className="px-3 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">Searching…</div>}
          {list.isSuccess && found.length === 0 && (
            <div className="px-3 py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">No skills match “{q}”</div>
          )}
          {found.map((skill) => {
            const linked = status(skill.id);
            return (
              <button
                key={skill.id}
                type="button"
                onClick={() => onPick(skill)}
                disabled={!!linked}
                title={skill.description}
                className="press w-full flex items-center gap-2.5 px-3 py-2 text-left min-w-0 hover:bg-[var(--accent-hover)] disabled:pointer-events-none"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className={`text-[13px] font-medium truncate ${linked ? "text-[var(--text-tertiary)]" : "text-[var(--text-primary)]"}`}>{skill.name}</span>
                    <SourceBadge source={skill.source} size={12} />
                  </span>
                  <span className="block text-[11.5px] text-[var(--text-tertiary)] truncate mt-[1px]">
                    {skill.repo ?? "Workspace skill"} · {skill.description}
                  </span>
                </span>
                {linked ? <Chip>{linked === "attached" ? "Attached" : "Requested"}</Chip> : <AuditShield audits={skill.audits} />}
              </button>
            );
          })}
          {list.hasNextPage && (
            <button
              type="button"
              onClick={() => list.fetchNextPage()}
              disabled={list.isFetchingNextPage}
              className="press w-full px-3 py-2 text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            >
              {list.isFetchingNextPage ? "Loading…" : "Show more"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function SkillConfirm({ skill, mode, agentId, sandboxOn, owner, requester, askedVersion, onBack, onDone }: {
  skill: Skill;
  mode: Mode;
  agentId: string;
  sandboxOn: boolean;
  owner: PersonRef;
  /** Who asked, when approving a request. */
  requester: PersonRef | null;
  /** The version the request is for: approving names it, so a newer one is never approved unseen. */
  askedVersion?: string;
  onBack?: () => void;
  onDone: () => void;
}) {
  const detail = useSkill(skill.id);
  const attach = useAttachSkill();
  const approve = useApproveSkill();
  const update = useUpdateSkillLink();
  const busy = attach.isPending || approve.isPending || update.isPending;
  // a recipe's yes waits until its steps are on screen, fresh: approving, as the request holds them
  const recipe = !!detail.data?.recipe;
  const steps = useSkillFile(skill.id, recipe ? "steps.json" : null, mode === "approve" ? agentId : undefined);
  // …and with the version they are of: without it the yes would name none, and approve anything
  const stepsRead = !recipe || (steps.isSuccess && !steps.isFetching && !!steps.data.version);
  // the yes names what is on screen: a recipe's steps came with their version (a cached copy
  // too: the server refuses it once it moved on, and the steps are read again); else the
  // request's version (approve) or the skill's whose audits show (update, attach)
  const read = recipe ? steps.data?.version ?? undefined : mode === "approve" ? askedVersion : detail.data?.version;

  const confirm = () => {
    const vars = { skillId: skill.id, agentId, version: read };
    if (mode === "approve") {
      approve.mutate(vars, { onSuccess: () => { toast.success(`${skill.name} approved — publish to let chats use it`); onDone(); } });
      return;
    }
    if (mode === "update") {
      update.mutate(vars, { onSuccess: () => { toast.success(`${skill.name} updated — publish to let chats use it`); onDone(); } });
      return;
    }
    attach.mutate(vars, {
      onSuccess: (link) => {
        toast.success(link.status === "attached"
          ? `${skill.name} attached — publish to let chats use it`
          : `Asked ${personName(owner)} to approve ${skill.name}`);
        onDone();
      },
    });
  };

  return (
    <>
      <div className="px-5 pt-5 pb-3 pr-12 flex items-start gap-2">
        {onBack && (
          <IconButton onClick={onBack} title="Back to the search" aria-label="Back to the search" className="-ml-1.5">
            <ChevronLeft className="w-3.5 h-3.5" strokeWidth={2} />
          </IconButton>
        )}
        <div className="min-w-0">
          <DialogTitle className="flex items-center gap-1.5 text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            <span className="truncate">{skill.name}</span>
            <SourceBadge source={skill.source} />
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5 truncate">
            {skill.repo ?? "Workspace skill"}
          </DialogDescription>
        </div>
      </div>

      <div className="px-5 pb-5 space-y-4 max-h-[60dvh] overflow-y-auto">
        <p className="m-0 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">{skill.description}</p>
        {detail.isPending ? (
          <div className="py-6 text-center text-[12.5px] text-[var(--text-quaternary)]">Reading its audits…</div>
        ) : detail.isError ? (
          <div className="py-6 text-center text-[12.5px] text-[var(--danger)]">Couldn't load this skill's audits.</div>
        ) : (
          <>
            {recipe && <RecipeSteps steps={steps} />}
            <Findings detail={detail.data} sandboxOn={sandboxOn} />
          </>
        )}
        <p className="m-0 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">
          {mode === "attach"
            ? "You own this agent, so it's attached at once, at your own risk. It goes on the draft: publish to let chats use it."
            : mode === "update"
              ? "The agent's new steps for this recipe, after the site changed: taking them puts exactly this version on the draft, at your own risk. Publish to let chats use it."
              : mode === "approve"
              ? detail.data?.recipe && !requester
                ? "The agent proposed this after a run that worked: these steps drive the person's browser, signed in as them. Approving attaches exactly this version, at your own risk. It goes on the draft: publish to let chats use it."
                : `${requester ? personName(requester) : "Someone"} asked for it. Approving attaches the version they asked for, at your own risk. It goes on the draft: publish to let chats use it.`
              : `${personName(owner)} owns this agent: it's added once they approve it.`}
        </p>
      </div>

      <div className="px-5 py-3.5 border-t hairline bg-[var(--surface)] flex items-center justify-end gap-2">
        <GhostButton type="button" onClick={onDone}>Cancel</GhostButton>
        <BrandButton type="button" onClick={confirm} disabled={busy || !detail.isSuccess || !stepsRead} className="disabled:opacity-40 disabled:pointer-events-none">
          {busy && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
          {PRIMARY[mode]}
        </BrandButton>
      </div>
    </>
  );
}

/** A recipe's steps, as the browser will take them: what the owner reads before saying yes. */
function RecipeSteps({ steps }: { steps: ReturnType<typeof useSkillFile> }) {
  if (steps.isPending) return <div className="py-3 text-center text-[12.5px] text-[var(--text-quaternary)]">Reading its steps…</div>;
  if (steps.isError) return <div className="py-3 text-center text-[12.5px] text-[var(--danger)]">Couldn't load its steps.</div>;
  return <CodeBlock code={steps.data.text ?? ""} lang="json" title="steps.json" />;
}

/** Every audit, or that none was done; and the sandbox the skill's scripts would need. */
function Findings({ detail, sandboxOn }: { detail: SkillDetail; sandboxOn: boolean }) {
  const audits = Object.values(detail.audits);
  return (
    <>
      <div>
        <div className="section-label mb-1.5">Security audits</div>
        {audits.length ? (
          <div className="rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] divide-y divide-[var(--hairline)] overflow-hidden">
            {audits.map((audit) => (
              <div key={audit.provider} className="flex items-center gap-2.5 px-3 py-2 min-w-0">
                <span className="shrink-0 text-[13px] text-[var(--text-primary)]">{audit.provider}</span>
                <span title={audit.summary ?? undefined} className="min-w-0 flex-1 truncate text-right text-[12px] text-[var(--text-tertiary)]">
                  {audit.summary ?? (audit.risk ? `Risk: ${audit.risk}` : "")}
                </span>
                <AuditPill status={audit.status} />
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-[12px] bg-[var(--surface-2)] px-3 py-2.5 text-[12.5px] leading-[1.5] text-[var(--text-secondary)]">
            <span className="font-medium text-[var(--text-primary)]">Not assessed</span> — no security auditor has checked this skill.
          </div>
        )}
      </div>
      {detail.needs_sandbox && (
        sandboxOn ? (
          <p className="m-0 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">It ships scripts: they run in this agent's sandbox.</p>
        ) : (
          <div className="flex items-start gap-2 rounded-[12px] bg-[var(--warn-bg)] ring-1 ring-[var(--warn-ring)] px-3 py-2.5 text-[12.5px] leading-[1.5] text-[var(--warn-text)]">
            <SquareTerminal className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={1.75} />
            <span><span className="font-medium">Needs a sandbox.</span> It ships scripts, and this agent's sandbox is off: it gets the instructions only.</span>
          </div>
        )
      )}
    </>
  );
}
